// axios.js
import axios from 'axios';
import {fetchAuthSession} from 'aws-amplify/auth';
import store from './store';
import {config} from './runtimeConfig';

const apiClient = axios.create({
    baseURL: config.backendBaseUrl,
    withCredentials: true,
    // Don't let a slow/dead backend hang the UI on a spinner forever.
    timeout: 30000,
});

apiClient.interceptors.request.use(
    async (cfg) => {
        // Prefer the token already in the store (set during the session restore).
        // If it's missing — e.g. a request fired before the restore finished —
        // fall back to Amplify so the request still authenticates instead of
        // going out anonymous. No cost in the normal (token-present) path.
        let token = store.state.auth.accessToken;
        if (!token) {
            try {
                const session = await fetchAuthSession();
                token = session && session.tokens && session.tokens.accessToken
                    ? session.tokens.accessToken.toString()
                    : null;
            } catch (e) {
                // Not signed in — proceed anonymously.
            }
        }
        if (token) {
            cfg.headers.Authorization = `Bearer ${token}`;
        }
        return cfg;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor: when an authenticated request comes back 401 (access
// token expired), refresh the session once and retry. If the refresh fails,
// clear the stale session so the app falls back to the logged-out state instead
// of looping on 401s. Anonymous callers (no session) are left untouched.
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const status = error.response && error.response.status;

        if (
            status === 401 &&
            original &&
            !original._retry &&
            store.getters['auth/isAuthenticated']
        ) {
            original._retry = true;
            try {
                const session = await fetchAuthSession({ forceRefresh: true });
                const token = session && session.tokens && session.tokens.accessToken
                    ? session.tokens.accessToken.toString()
                    : null;
                if (token) {
                    store.commit('auth/SET_ACCESS_TOKEN', token);
                    original.headers = original.headers || {};
                    original.headers.Authorization = `Bearer ${token}`;
                    return apiClient(original);
                }
            } catch (e) {
                // Refresh failed — fall through to clearing the session.
            }
            try {
                await store.dispatch('auth/logout');
            } catch (e) {
                // best-effort
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;